import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLayout } from "../../contexts/LayoutContext";
import ResizablePanel from "../../components/common/ResizablePanel";
import PropertyListPanel from "../properties/panels/PropertyListPanel";
import { PROPERTY_COLLECTION_KEYS } from "../../graphQL/properties";
import { toPropertyCard } from "../properties/presenters";
import { usePropertyCollection } from "../properties/usePropertyCollection";
import LeadContactListPanel from "./LeadContactListPanel";
import ContactDetailPanel from "../addressbook/panels/ContactDetailPanel";
import CommentsPanel from "../addressbook/panels/CommentsPanel";
import JournalPanel from "../addressbook/panels/JournalPanel";
import RemindersPanel from "../addressbook/panels/RemindersPanel";
import PropertyDetailPanel from "../properties/panels/PropertyDetailPanel";
import PropertyChatPanel from "../properties/panels/PropertyChatPanel";
import PropertyMapPanel from "../properties/panels/PropertyMapPanel";
import InterestMapCard from "./InterestMapCard";
import { apiUpdatePropertyAsset, apiDeletePropertyAsset, apiGetPropertyAsset } from "../../graphQL/properties/collections/propertyAssets";
import { apiCreateContact, apiUpdateContact, apiDeleteContact, apiGetContactById } from "../../graphQL/addressbook/collections/contacts";
import ComposeWithMagicDialog from "../../components/spark/ComposeWithMagicDialog";
import ComposeTextDialog from "../../components/spark/ComposeTextDialog";
import { usePropertyCategories } from "../properties/usePropertyCategories";
import FilesPanel from "../properties/panels/FilesPanel";
import { useUploadContext } from "../../Provider/UploadContext";

const PROPERTY_REF_COLLECTION = "propertyAssets";

const formatPropertyLabel = (property) => {
  const address = property?.identity?.address || {};
  const street = [address.streetNo, address.streetName].filter(Boolean).join(" ") || address.street || "";
  const apn = property?.identity?.apn || "";
  return street || property?.name || apn || "";
};

const AVAILABLE_PROPERTY_STATUSES = [
  "Available",
  "Under Contract",
  "Sold",
  "Leased",
  "Off Market",
];

const COLLECTION_KEY = PROPERTY_COLLECTION_KEYS.PROPERTY_ASSETS;
const EMPTY_CONTACT_DRAFT = {
  identity: { person: { firstName: "", lastName: "" }, company: {} },
  reach: { emails: [], phones: [], social: [], addresses: [] },
};

const toRad = (v) => (Number(v) * Math.PI) / 180;
const milesBetween = (aLat, aLng, bLat, bLng) => {
  const R = 3958.8;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const x =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  return R * c;
};

const toList = (value) => {
  if (Array.isArray(value)) {
    return value.map((v) => String(v || "").trim()).filter(Boolean);
  }
  return String(value || "")
    .split(/[,\n;]+/)
    .map((v) => v.trim())
    .filter(Boolean);
};

const normalizeZip = (value) => String(value || "").trim().toLowerCase().split("-")[0];

const PageMatch = () => {
  const user = useSelector((state) => state.auth.user);
  const authorId = useSelector((state) => state.auth.userId);
  const userDomain = String(user?.domain || "").trim();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isPanelOpen, togglePanel, setPanels, setMatchToolbar, resetMatchToolbar } = useLayout();
  const { uploadSingle } = useUploadContext() || {};

  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [contactSearchInput, setContactSearchInput] = useState("");
  const [contactSearchFilter, setContactSearchFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showContactDetails, setShowContactDetails] = useState(false);
  const [composeContact, setComposeContact] = useState(null);
  const [composeTextContact, setComposeTextContact] = useState(null);

  // Match doesn't expose Journal/Reminders in its AppBar. Force them closed on
  // mount so the global default (journal: true) doesn't bleed in from other pages,
  // and the Details panel keeps the full right-side slot.
  useEffect(() => {
    setPanels({ journal: false, reminders: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contactsOpen = isPanelOpen("contacts");
  const detailsOpen = isPanelOpen("details");
  const mapOpen = isPanelOpen("map");
  const commentsOpen = isPanelOpen("comments");
  const chatOpen = isPanelOpen("chat");
  const journalOpen = isPanelOpen("journal");
  const remindersOpen = isPanelOpen("reminders");
  const filesOpen = isPanelOpen("files");

  // Files panel state
  const [filesDisplayName, setFilesDisplayName] = useState("");
  const [filesSelectedType, setFilesSelectedType] = useState("");
  const [filesSelectedFile, setFilesSelectedFile] = useState(null);
  const [filesStatus, setFilesStatus] = useState("");
  const [filesProgress, setFilesProgress] = useState(0);

  const lastPanel =
    filesOpen ? "files" :
    remindersOpen ? "reminders" :
    journalOpen ? "journal" :
    chatOpen ? "chat" :
    commentsOpen ? "comments" :
    mapOpen ? "map" :
    detailsOpen ? "details" :
    contactsOpen ? "contacts" :
    null;

  const pagination = useMemo(() => ({ page, limit: rowsPerPage }), [page, rowsPerPage]);
  const ordering = useMemo(() => ({ field: "updatedAt", order: "Desc" }), []);
  const activeDomain = userDomain && userDomain !== "All" ? userDomain : "";

  const filters = useMemo(() => {
    const f = {};
    if (searchFilter) f.query = searchFilter;
    if (activeDomain) f.domain = activeDomain;
    if (categoryFilter) f.category = categoryFilter;
    return f;
  }, [searchFilter, activeDomain, categoryFilter]);
  const activeFilters = useMemo(() => {
    const list = [];
    if (categoryFilter) list.push(`category=${categoryFilter}`);
    if (searchFilter) list.push(`query="${searchFilter}"`);
    return list;
  }, [searchFilter, categoryFilter]);

  const { loading, error, total, items, patchItem, removeItem } = usePropertyCollection({
    authorId: null,
    collectionKey: COLLECTION_KEY,
    pagination,
    ordering,
    filters,
    allowWithoutAuthorId: true,
  });

  const handleMatchFileSave = useCallback(async (variables) => {
    try {
      if (!variables?.id) return variables;
      const result = await apiUpdatePropertyAsset({ id: variables.id });
      if (result?.id) patchItem(result.id, result);
      return result;
    } catch (err) {
      console.error("Error saving match files:", err);
      throw err;
    }
  }, [patchItem]);

  // Full document (incl. `details`) for the selected property — list items
  // are slim, so detail-side panels hydrate from a single-doc fetch.
  const [selectedPropertyFull, setSelectedPropertyFull] = useState(null);
  useEffect(() => {
    const id = selectedProperty?.id;
    if (!id || String(id).startsWith("draft-")) {
      setSelectedPropertyFull(null);
      return undefined;
    }
    let cancelled = false;
    apiGetPropertyAsset(id)
      .then((doc) => {
        if (!cancelled && doc?.id === id) setSelectedPropertyFull(doc);
      })
      .catch((err) => console.error("Failed to load property details:", err));
    return () => { cancelled = true; };
  }, [selectedProperty?.id]);

  const selectedPropertyRaw = useMemo(() => {
    if (!selectedProperty) return null;
    const listItem = items.find((p) => p.id === selectedProperty.id) || null;
    const full = selectedPropertyFull?.id === selectedProperty.id ? selectedPropertyFull : null;
    if (listItem && full) return { ...full, ...listItem };
    return listItem || full;
  }, [selectedProperty, items, selectedPropertyFull]);

  // Contact rows from LeadContactListPanel are slim (no `data` bag). Interest
  // matching and interest-pin saves read/WRITE contact.data, so hydrate the
  // selected contact with its full document.
  useEffect(() => {
    const id = selectedContact?.id;
    if (!id || selectedContact?._isNew || String(id).startsWith("draft-")) return undefined;
    let cancelled = false;
    apiGetContactById({ id, authorId })
      .then((doc) => {
        if (!cancelled && doc?.id) {
          setSelectedContact((prev) => (prev?.id === doc.id ? { ...prev, ...doc } : prev));
        }
      })
      .catch((err) => console.error("Failed to load contact details:", err));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact?.id, selectedContact?._isNew, authorId]);
  const settingsAuthorId = user?.id || user?.userId || "";
  const propertyCategories = usePropertyCategories(settingsAuthorId);

  const handleStatusChange = useCallback(
    async (property, nextStatus) => {
      if (!property?.id) return;
      const value = String(nextStatus || "");
      patchItem(property.id, { status: value });
      try {
        // NOTE: list items are slim (no `details`), so never send a details
        // payload from here — it would replace the stored details object.
        await apiUpdatePropertyAsset({ id: property.id, status: value });
      } catch (err) {
        console.error("Failed to update property status:", err);
      }
    },
    [patchItem]
  );

  const handleCategoryChange = useCallback(
    async (property, category) => {
      if (!property?.id || !category) return;
      const value = category.id || category.value || category.name || "";
      patchItem(property.id, { category: value });
      try {
        await apiUpdatePropertyAsset({ id: property.id, category: value });
      } catch (err) {
        console.error("Failed to update property category:", err);
      }
    },
    [patchItem]
  );

  const handleSaveProperty = useCallback(
    async (updated) => {
      if (!updated?.id) return;
      patchItem(updated.id, updated);
      try {
        const { id, name, status, category, identity, details } = updated;
        await apiUpdatePropertyAsset({ id, name, status, category, identity, details });
      } catch (err) {
        console.error("Failed to save property:", err);
      }
    },
    [patchItem]
  );

  const handleDeleteProperty = useCallback(
    async (property) => {
      if (!property?.id) return;
      const originalId = property.id;
      removeItem(originalId);
      if (selectedProperty?.id === originalId) setSelectedProperty(null);
      setSelectedPropertyIds((prev) => prev.filter((id) => id !== originalId));
      try {
        await apiDeletePropertyAsset(originalId);
      } catch (err) {
        console.error("Failed to delete property:", err);
      }
    },
    [removeItem, selectedProperty?.id]
  );
  const handleTogglePropertySelection = useCallback((property) => {
    const id = property?.id;
    if (!id) return;
    setSelectedPropertyIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);
  const handleAddPropertyContact = useCallback(() => {
    setSelectedContact({
      id: `draft-${Date.now()}`,
      ...EMPTY_CONTACT_DRAFT,
      _isNew: true,
    });
    setShowContactDetails(true);
    setPanels({ details: true, map: false, comments: false, chat: false });
  }, [setPanels]);

  const handleSavePropertyContact = useCallback(async (changes) => {
    if (!selectedContact) return;
    try {
      if (selectedContact._isNew) {
        const created = await apiCreateContact({
          authorId,
          ...EMPTY_CONTACT_DRAFT,
          ...changes,
        });
        const createdDoc = created?.contact || created;
        if (createdDoc?.id) {
          setSelectedContact(createdDoc);
        }
      } else if (selectedContact?.id) {
        await apiUpdateContact({ id: selectedContact.id, ...changes });
      }
    } catch (err) {
      console.error("Failed to save contact:", err);
    }
  }, [authorId, selectedContact]);

  const handleSaveInterestPins = useCallback(async (contact, pins) => {
    if (!contact?.id) return;
    const cleanPins = (Array.isArray(pins) ? pins : [])
      .map((p) => ({
        latitude: Number(p?.latitude),
        longitude: Number(p?.longitude),
        radiusMiles: Number(p?.radiusMiles) || 5,
      }))
      .filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude));
    const nextData = { ...(contact?.data || {}) };
    nextData.interests = { ...(nextData?.interests || {}), geoLocations: cleanPins };
    setSelectedContact((prev) => (prev?.id === contact.id ? { ...prev, data: nextData } : prev));
    try {
      await apiUpdateContact({ id: contact.id, data: nextData });
    } catch (err) {
      console.error("Failed to save interest pins:", err);
    }
  }, []);

  const handleDeletePropertyContact = useCallback(async (contactId) => {
    if (!contactId) return;
    if (String(contactId).startsWith("draft-")) {
      setSelectedContact(null);
      setShowContactDetails(false);
      return;
    }
    try {
      await apiDeleteContact({ id: contactId, authorId });
      setSelectedContact(null);
      setShowContactDetails(false);
    } catch (err) {
      console.error("Failed to delete contact:", err);
    }
  }, [authorId]);
  const properties = useMemo(() => {
    const cards = items.map(toPropertyCard);
    const interests =
      (selectedContact?.data && typeof selectedContact.data === "object" && selectedContact.data.interests) ||
      selectedContact?.interests ||
      {};
    if (!selectedContact?.id) return cards;

    // Hard linkage takes precedence: if the contact has propertyListIds
    // (top-level reverse index from propertyAssets.contactList), filter to
    // those properties only. This shows the user the properties this
    // contact is actually attached to, instead of the interest-based
    // recommendation.
    const linked = Array.isArray(selectedContact?.propertyListIds)
      ? selectedContact.propertyListIds
      : [];
    if (linked.length > 0) {
      const linkedSet = new Set(linked.map((x) => String(x)));
      return cards.filter((p) => linkedSet.has(String(p?.id)));
    }

    const statusNeedle = String(interests?.status || "").trim().toLowerCase();
    const categoryNeedle = String(interests?.category || "").trim().toLowerCase();
    const citySet = new Set(toList(interests?.geoCities).map((v) => v.toLowerCase()));
    const zipSet = new Set(toList(interests?.geoZipcodes).map(normalizeZip));
    const geoLocations = Array.isArray(interests?.geoLocations) ? interests.geoLocations : [];

    return cards.filter((p) => {
      const addr = p?.identity?.address || {};
      const textStatus = String(p?.status || "").toLowerCase();
      const textCategory = String(p?.category || "").toLowerCase();
      const city = String(addr?.city || "").trim().toLowerCase();
      const propertyZip = normalizeZip(p?.zip || addr?.zip);

      if (statusNeedle && !textStatus.includes(statusNeedle)) return false;
      if (categoryNeedle && !textCategory.includes(categoryNeedle)) return false;
      if (citySet.size > 0 && !citySet.has(city)) return false;
      if (zipSet.size > 0 && !zipSet.has(propertyZip)) return false;

      if (geoLocations.length > 0) {
        if (!p?.geolocation) return false;
        const { latitude: lat, longitude: lng } = p.geolocation;
        const inside = geoLocations.some((g) => {
          const gLat = Number(g?.latitude);
          const gLng = Number(g?.longitude);
          const radius = Number(g?.radiusMiles) || 0;
          if (!Number.isFinite(gLat) || !Number.isFinite(gLng) || radius <= 0) return false;
          return milesBetween(lat, lng, gLat, gLng) <= radius;
        });
        if (!inside) return false;
      }
      return true;
    });
  }, [items, selectedContact]);
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  useEffect(() => {
    const validIds = new Set(items.map((p) => p.id).filter(Boolean));
    setSelectedPropertyIds((prev) => prev.filter((id) => validIds.has(id)));
  }, [items]);

  const handlePreviousPage = () => {
    setPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setPage((prev) => (prev < totalPages ? prev + 1 : prev));
  };

  const handleSearchSubmit = useCallback(() => {
    setSearchFilter(searchInput.trim());
    setPage(1);
  }, [searchInput]);

  const handleSearchChange = useCallback((v) => {
    setSearchInput(v);
    if (!v.trim()) {
      setSearchFilter("");
      setPage(1);
    }
  }, []);
  const handleContactSearchSubmit = useCallback(() => {
    setContactSearchFilter(contactSearchInput.trim());
  }, [contactSearchInput]);
  const handleContactSearchChange = useCallback((v) => {
    setContactSearchInput(v);
    if (!v.trim()) setContactSearchFilter("");
  }, []);

  useEffect(() => {
    setMatchToolbar({
      propertySearchQuery: searchInput || "",
      onPropertySearchChange: handleSearchChange,
      onPropertySearchSubmit: handleSearchSubmit,
      contactSearchQuery: contactSearchInput || "",
      onContactSearchChange: handleContactSearchChange,
      onContactSearchSubmit: handleContactSearchSubmit,
    });
    return () => {
      resetMatchToolbar();
    };
  }, [searchInput, contactSearchInput, handleSearchChange, handleSearchSubmit, handleContactSearchChange, handleContactSearchSubmit, setMatchToolbar, resetMatchToolbar]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        overflow: "hidden",
        gap: isMobile ? 0.75 : 1,
      }}
    >
      <ResizablePanel
        edge="right"
        defaultWidth={isMobile ? window.innerWidth : 420}
        minWidth={isMobile ? window.innerWidth : 280}
        maxWidth={isMobile ? window.innerWidth : 640}
        fillRemaining={isMobile || lastPanel === null}
        sx={isMobile ? { width: "100% !important" } : undefined}
      >
        <PropertyListPanel
          loading={loading}
          error={error}
          properties={properties}
          total={total}
          page={page}
          totalPages={totalPages}
          canPrevious={page > 1}
          canNext={page < totalPages}
          onPrevious={handlePreviousPage}
          onNext={handleNextPage}
          searchQuery={searchInput}
          onSearchChange={handleSearchChange}
          onSearchSubmit={handleSearchSubmit}
          selectedId={selectedProperty?.id}
          selectedIds={selectedPropertyIds}
          onSelect={setSelectedProperty}
          onOpenDetails={(property) => {
            setSelectedProperty(property);
            setShowContactDetails(false);
            setPanels({ details: true, map: false, comments: false, chat: false });
          }}
          onToggleSelect={handleTogglePropertySelection}
          activeFilters={activeFilters}
          categories={propertyCategories}
          availableStatuses={AVAILABLE_PROPERTY_STATUSES}
          onStatusChange={handleStatusChange}
          onCategoryChange={handleCategoryChange}
          onDeleteProperty={handleDeleteProperty}
          pageSize={rowsPerPage}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={(value) => {
            setCategoryFilter(value || "");
            setPage(1);
          }}
        />
      </ResizablePanel>

      {contactsOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 360}
          minWidth={isMobile ? window.innerWidth : 280}
          maxWidth={isMobile ? window.innerWidth : 560}
          fillRemaining={!isMobile && lastPanel === "contacts"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <LeadContactListPanel
            property={selectedPropertyRaw}
            authorId={authorId}
            onClose={() => togglePanel("contacts")}
            selectedId={selectedContact?.id}
            onSelect={(contact) => {
              setSelectedContact(contact);
              setShowContactDetails(false);
            }}
            onAdd={handleAddPropertyContact}
            onOpenDetails={(contact) => {
              setSelectedContact(contact);
              setShowContactDetails(true);
              setPanels({ details: true, map: false, comments: false, chat: false });
            }}
            onCompose={(contact) => {
              setComposeContact(contact);
            }}
            onComposeText={(contact) => {
              setComposeTextContact(contact);
            }}
            onMagic={() => {
              if (selectedContact) {
                setComposeContact(selectedContact);
              }
            }}
            onDelete={handleDeletePropertyContact}
            searchQuery={contactSearchInput}
            onSearchChange={handleContactSearchChange}
            onSearchSubmit={handleContactSearchSubmit}
            searchFilter={contactSearchFilter}
          />
        </ResizablePanel>
      )}

      {detailsOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 700}
          minWidth={isMobile ? window.innerWidth : 300}
          maxWidth={isMobile ? window.innerWidth : 800}
          fillRemaining={!isMobile && lastPanel === "details"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          {showContactDetails ? (
            <ContactDetailPanel
              contact={selectedContact}
              onClose={() => togglePanel("details")}
              onSave={handleSavePropertyContact}
              onDelete={handleDeletePropertyContact}
              extraTopContent={
                selectedContact ? (
                  <InterestMapCard
                    contact={selectedContact}
                    onSaveInterests={handleSaveInterestPins}
                  />
                ) : null
              }
            />
          ) : (
            <PropertyDetailPanel
              property={selectedPropertyRaw}
              onSave={handleSaveProperty}
              onClose={() => togglePanel("details")}
            />
          )}
        </ResizablePanel>
      )}

      {filesOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 560}
          minWidth={isMobile ? window.innerWidth : 320}
          maxWidth={isMobile ? window.innerWidth : 900}
          fillRemaining={!isMobile && lastPanel === "files"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <FilesPanel
            onClose={() => togglePanel("files")}
            propertyInfo={selectedPropertyRaw || { id: "", details: { files: [] } }}
            updatePropertyInfo={() => {}}
            uploadSingle={uploadSingle}
            actionSave={handleMatchFileSave}
            filesDisplayName={filesDisplayName}
            setFilesDisplayName={setFilesDisplayName}
            filesSelectedType={filesSelectedType}
            setFilesSelectedType={setFilesSelectedType}
            filesSelectedFile={filesSelectedFile}
            setFilesSelectedFile={setFilesSelectedFile}
            filesStatus={filesStatus}
            setFilesStatus={setFilesStatus}
            filesProgress={filesProgress}
            setFilesProgress={setFilesProgress}
            fileSettingsType="property"
            fileRefCollection="propertyAssets"
            fileRefSchemaType="propertyAsset"
            emptySelectionMessage="Select property first"
            previewEntityType="property"
            entityName={selectedPropertyRaw ? formatPropertyLabel(selectedPropertyRaw) : ""}
          />
        </ResizablePanel>
      )}

      {mapOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 700}
          minWidth={isMobile ? window.innerWidth : 300}
          maxWidth={isMobile ? window.innerWidth : 800}
          fillRemaining={!isMobile && lastPanel === "map"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <PropertyMapPanel
            selectedProperties={properties.filter((p) => selectedPropertyIds.includes(p.id))}
            fallbackProperty={selectedPropertyRaw}
            onClose={() => togglePanel("map")}
          />
        </ResizablePanel>
      )}

      {commentsOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 560}
          minWidth={isMobile ? window.innerWidth : 300}
          maxWidth={isMobile ? window.innerWidth : 800}
          fillRemaining={!isMobile && lastPanel === "comments"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <CommentsPanel
            refCollection={PROPERTY_REF_COLLECTION}
            refId={selectedPropertyRaw?.id || null}
            refLabel={selectedPropertyRaw ? formatPropertyLabel(selectedPropertyRaw) : ""}
            emptyMessage="Select a property to view comments."
            authorId={authorId}
            onClose={() => togglePanel("comments")}
          />
        </ResizablePanel>
      )}

      {chatOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 560}
          minWidth={isMobile ? window.innerWidth : 300}
          maxWidth={isMobile ? window.innerWidth : 800}
          fillRemaining={!isMobile && lastPanel === "chat"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <PropertyChatPanel
            property={selectedPropertyRaw}
            authorId={authorId}
            onClose={() => togglePanel("chat")}
          />
        </ResizablePanel>
      )}

      {journalOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 380}
          minWidth={isMobile ? window.innerWidth : 280}
          maxWidth={isMobile ? window.innerWidth : 500}
          fillRemaining={!isMobile && lastPanel === "journal"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <JournalPanel
            refCollection={PROPERTY_REF_COLLECTION}
            refId={selectedPropertyRaw?.id || null}
            refName={selectedPropertyRaw ? formatPropertyLabel(selectedPropertyRaw) : ""}
            emptyMessage="Select a property to view its journal"
            authorId={authorId}
            onClose={() => togglePanel("journal")}
          />
        </ResizablePanel>
      )}

      {remindersOpen && (
        <ResizablePanel
          edge="right"
          defaultWidth={isMobile ? window.innerWidth : 380}
          minWidth={isMobile ? window.innerWidth : 280}
          maxWidth={isMobile ? window.innerWidth : 500}
          fillRemaining={!isMobile && lastPanel === "reminders"}
          sx={isMobile ? { width: "100% !important" } : undefined}
        >
          <RemindersPanel
            authorId={authorId}
            refCollection={PROPERTY_REF_COLLECTION}
            onClose={() => togglePanel("reminders")}
          />
        </ResizablePanel>
      )}

      <ComposeWithMagicDialog
        open={Boolean(composeContact)}
        onClose={() => setComposeContact(null)}
        contact={composeContact}
      />
      <ComposeTextDialog
        open={Boolean(composeTextContact)}
        onClose={() => setComposeTextContact(null)}
        contact={composeTextContact}
      />
    </Box>
  );
};

export default PageMatch;
